package com.dotdrives.web.pages.drives

import com.dotdrives.controller.NewDriveController
import com.dotdrives.controller.NewDriveUiState
import com.dotdrives.design.DotAvatarPalette
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.DrivePhaseTemplate
import com.dotdrives.model.OrgMember
import com.dotdrives.model.Target
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

external interface NewDriveDialogProps : Props {
    var controller: NewDriveController
    var state: NewDriveUiState
    var templates: List<DrivePhaseTemplate>
    var targets: List<Target>
    var members: List<OrgMember>
    var onCancel: () -> Unit
}

val NewDriveDialog = FC<NewDriveDialogProps> { props ->
    com.dotdrives.web.ui.useDialogFocus(true, "new-drive-dialog", props.onCancel)
    val state = props.state
    val controller = props.controller

    div {
        className = ClassName("drives-dialog-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onCancel() }

        div {
            id = web.dom.ElementId("new-drive-dialog")
            className = ClassName("drives-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "New drive"
            onClick = { it.stopPropagation() }

            div {
                className = ClassName("drives-dialog-head")
                div { className = ClassName("drives-dialog-title"); +"New drive" }
                button {
                    type = ButtonType.buttonType
                    className = ClassName("drives-dialog-close")
                    ariaLabel = "Cancel"
                    onClick = { props.onCancel() }
                    +"✕"
                }
            }

            state.error?.let { error ->
                DotBanner { message = error; className = "drives-inline-banner" }
            }

            div {
                className = ClassName("drives-form-field")
                div { className = ClassName("drives-form-label"); +"Giver" }
                div {
                    className = ClassName("drives-giver-search-wrap")
                    input {
                        type = InputType.text
                        value = state.giverQuery
                        placeholder = "Search givers…"
                        ariaLabel = "Search givers"
                        onChange = { controller.searchGiver(it.currentTarget.value) }
                    }
                }
            }

            if (state.selectedGiver == null && state.giverQuery.isNotBlank()) {
                div {
                    className = ClassName("drives-giver-results")
                    state.giverResults.forEach { giver ->
                        val (bg, fg) = DotAvatarPalette.tintFor(giver.id)
                        val avatarStyle = js("({})")
                        avatarStyle.backgroundColor = bg
                        avatarStyle.color = com.dotdrives.web.ui.accessibleAvatarText(fg)
                        button {
                            key = giver.id.unsafeCast<Key>()
                            type = ButtonType.buttonType
                            className = ClassName("drives-giver-result")
                            onClick = { controller.selectGiver(giver) }
                            div {
                                className = ClassName("drives-card-avatar")
                                style = avatarStyle
                                +driveInitials(giver.displayName)
                            }
                            div {
                                className = ClassName("drives-giver-result-copy")
                                div { className = ClassName("drives-giver-result-name"); +giver.displayName }
                                div {
                                    className = ClassName("drives-giver-result-meta")
                                    +giver.giverType.replaceFirstChar(Char::uppercase)
                                }
                            }
                        }
                    }
                    button {
                        type = ButtonType.buttonType
                        className = ClassName("drives-giver-create-row")
                        onClick = { controller.createGiverInline(state.giverQuery) }
                        +"+ Create \"${state.giverQuery}\" as a new giver"
                    }
                }
            } else if (state.selectedGiver != null) {
                val giver = state.selectedGiver!!
                val (bg, fg) = DotAvatarPalette.tintFor(giver.id)
                val avatarStyle = js("({})")
                avatarStyle.backgroundColor = bg
                avatarStyle.color = com.dotdrives.web.ui.accessibleAvatarText(fg)
                div {
                    className = ClassName("drives-selected-giver")
                    div {
                        className = ClassName("drives-card-avatar")
                        style = avatarStyle
                        +driveInitials(giver.displayName)
                    }
                    div {
                        className = ClassName("drives-giver-result-copy")
                        div { className = ClassName("drives-giver-result-name"); +giver.displayName }
                        div {
                            className = ClassName("drives-giver-result-meta")
                            +giver.giverType.replaceFirstChar(Char::uppercase)
                        }
                    }
                    button {
                        type = ButtonType.buttonType
                        className = ClassName("drives-dialog-close")
                        ariaLabel = "Change giver"
                        onClick = { controller.searchGiver("") }
                        +"✕"
                    }
                }
            }

            div {
                className = ClassName("drives-form-grid")
                div {
                    className = ClassName("drives-form-field")
                    div { className = ClassName("drives-form-label"); +"Donation target" }
                    select {
                        ariaLabel = "Donation target"
                        value = state.targetId ?: ""
                        onChange = { controller.setTargetId(it.currentTarget.value.takeIf(String::isNotBlank)) }
                        option { value = ""; +"No target" }
                        props.targets.forEach { target ->
                            option { key = target.id.unsafeCast<Key>(); value = target.id; +target.name }
                        }
                    }
                }
                div {
                    className = ClassName("drives-form-field")
                    div { className = ClassName("drives-form-label"); +"Ask amount" }
                    input {
                        type = InputType.text
                        value = state.askAmount
                        placeholder = "8,000"
                        ariaLabel = "Ask amount"
                        onChange = { controller.setAskAmount(it.currentTarget.value) }
                    }
                }
            }

            div {
                className = ClassName("drives-form-grid")
                div {
                    className = ClassName("drives-form-field")
                    div { className = ClassName("drives-form-label"); +"Process template" }
                    select {
                        ariaLabel = "Process template"
                        value = state.templateId ?: ""
                        onChange = { controller.setTemplateId(it.currentTarget.value) }
                        props.templates.forEach { template ->
                            option { key = template.id.unsafeCast<Key>(); value = template.id; +template.name }
                        }
                    }
                }
                div {
                    className = ClassName("drives-form-field")
                    div { className = ClassName("drives-form-label"); +"Steward" }
                    select {
                        ariaLabel = "Steward"
                        value = state.stewardUserId ?: ""
                        onChange = { controller.setStewardUserId(it.currentTarget.value) }
                        props.members.forEach { member ->
                            option { key = member.userId.unsafeCast<Key>(); value = member.userId; +member.displayName }
                        }
                    }
                }
            }

            val templateName = props.templates.firstOrNull { it.id == state.templateId }?.name ?: "the default process"
            div {
                className = ClassName("drives-preview-note")
                if (state.firstPhaseName != null) {
                    +"Starts in "
                    react.dom.html.ReactHTML.b { +state.firstPhaseName!! }
                    +" with ${state.firstPhaseTaskCount} phase task${if (state.firstPhaseTaskCount == 1) "" else "s"} from the "
                    react.dom.html.ReactHTML.b { +templateName }
                    +" template."
                } else if (state.initialPhaseId != null) {
                    +"This drive starts in the selected phase of "
                    react.dom.html.ReactHTML.b { +templateName }
                    +"."
                } else {
                    +"This drive starts in the first phase of "
                    react.dom.html.ReactHTML.b { +templateName }
                    +"."
                }
            }

            div {
                className = ClassName("drives-dialog-actions")
                DotButton {
                    text = "Cancel"
                    variant = DotButtonVariant.Secondary
                    onClick = props.onCancel
                    enabled = !state.saving
                }
                DotButton {
                    text = "Create drive"
                    onClick = controller::save
                    loading = state.saving
                    enabled = state.selectedGiver != null
                }
            }
        }
    }
}
